import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import UnderlineExt from '@tiptap/extension-underline'
import LinkExt from '@tiptap/extension-link'
import Highlight from '@tiptap/extension-highlight'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import {
  Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, CheckSquare,
  Highlighter, Link, Undo2, Redo2,
  Send, Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  onSubmit: (html: string) => void
  isPending?: boolean
  placeholder?: string
}

export default function RichNoteEditor({ onSubmit, isPending, placeholder = 'Scrivi una nota...' }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
      }),
      Placeholder.configure({ placeholder }),
      UnderlineExt,
      Highlight.configure({ multicolor: false }),
      LinkExt.configure({ openOnClick: false, autolink: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[40px] px-3 py-2 text-sm text-slate-800',
      },
    },
  })

  const handleSubmit = () => {
    if (!editor || isPending) return
    const html = editor.getHTML()
    // Check if editor is empty (only <p></p> or whitespace)
    const text = editor.getText().trim()
    if (!text) return
    onSubmit(html)
    editor.commands.clearContent()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (!editor) return null

  const hasContent = editor.getText().trim().length > 0

  const ToolBtn = ({ active, onClick, children, title }: { active?: boolean; onClick: () => void; children: React.ReactNode; title: string }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'p-1 rounded transition-colors',
        active ? 'bg-accent-100 text-accent-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
      )}
    >
      {children}
    </button>
  )

  const addLink = () => {
    const url = prompt('URL:')
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-accent-500/30 focus-within:border-accent-400 transition-all">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-slate-100 bg-slate-50/50 flex-wrap">
        <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Grassetto">
          <Bold className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Corsivo">
          <Italic className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Sottolineato">
          <Underline className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barrato">
          <Strikethrough className="w-3.5 h-3.5" />
        </ToolBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolBtn active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Evidenzia">
          <Highlighter className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('link')} onClick={addLink} title="Link">
          <Link className="w-3.5 h-3.5" />
        </ToolBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista puntata">
          <List className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerata">
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()} title="Checklist">
          <CheckSquare className="w-3.5 h-3.5" />
        </ToolBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Annulla">
          <Undo2 className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Ripeti">
          <Redo2 className="w-3.5 h-3.5" />
        </ToolBtn>

        <div className="flex-1" />

        {hasContent && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isPending}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 transition-colors disabled:opacity-50"
          >
            {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Pubblica
          </button>
        )}
      </div>

      {/* Editor */}
      <div onKeyDown={handleKeyDown}>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
